import { useEffect, useId, useState, type ChangeEvent } from "react";
import {
  useCompanyLogo,
  useCompanySettings,
  useSaveCompanySettings,
  useUploadCompanyLogo,
  type CompanySettings,
} from "@/api/settings";
import { CompanyForm } from "@/components/forms/CompanyForm";
import { RequestError } from "@/components/RequestError";
import { buttonClass } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

function LogoCard({ company, onReload }: { company: CompanySettings; onReload: () => void }) {
  const logo = useCompanyLogo(company.logo_sha256);
  const upload = useUploadCompanyLogo();
  const [url, setUrl] = useState<string | null>(null);
  const inputId = useId();

  useEffect(() => {
    if (!logo.data) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(logo.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [logo.data]);

  function onFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) upload.mutate({ file, version: company.version });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Logo</CardTitle>
        <CardDescription>PNG or JPEG up to 2 MB, printed at the top of invoices.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex h-28 items-center justify-center rounded-md border bg-background p-3">
          {url ? (
            <img src={url} alt="Company logo" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-sm text-muted-foreground">
              {company.logo_sha256 ? "Loading logo…" : "No logo uploaded"}
            </span>
          )}
        </div>
        <RequestError error={upload.error} onReload={onReload} />
        <label
          htmlFor={inputId}
          className={buttonClass("outline", "sm")}
          aria-disabled={upload.isPending || undefined}
        >
          {upload.isPending ? "Uploading…" : "Upload a new logo"}
        </label>
        <input
          id={inputId}
          type="file"
          accept="image/png,image/jpeg"
          className="sr-only"
          disabled={upload.isPending}
          onChange={onFile}
        />
      </CardContent>
    </Card>
  );
}

export function CompanyPanel() {
  const company = useCompanySettings();
  const save = useSaveCompanySettings();

  if (company.isPending) {
    return <p className="text-muted-foreground">Loading company settings…</p>;
  }
  if (company.isError) {
    return <RequestError error={company.error} />;
  }

  const data = company.data;
  const reload = () => void company.refetch();

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle>Company details</CardTitle>
          <CardDescription>
            Printed on every invoice. Issued invoices keep the details they were issued with.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CompanyForm
            key={data.version}
            initial={data}
            onSubmit={(input) => save.mutateAsync({ input, version: data.version })}
            onReload={reload}
          />
        </CardContent>
      </Card>
      <LogoCard company={data} onReload={reload} />
    </div>
  );
}
